import * as React from 'react'
import { AlertTriangle, Check, Mic, Play } from 'lucide-react'
import { GENDERS, type Gender } from '@acs/shared'
import { cn } from '@/lib/cn'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'

/**
 * The question Start asks before it will start anyone without a gender.
 *
 * ── Why a dialog, and why HERE ──────────────────────────────────────
 * The audio assessment speaks in a voice chosen by `customers.gender`, and it
 * has no default. A customer without one runs the whole way — sign-in, hunt,
 * the shift actually won — and then stops dead at the recorded questions, hours
 * after the operator pressed Start and long after anyone was watching. So
 * `POST /start` refuses them with `gender_required`, and this dialog is what
 * turns that refusal into a click: it names every customer in the batch that
 * lacks one, takes the answer for each, and hands the whole batch back to be
 * started in one go, with each answer riding on its own customer's request.
 *
 * ── Built for the batch ─────────────────────────────────────────────
 * "Start selected" over fifty rows is the case this exists for, and two things
 * follow from it. The gate reads `gender` OFF THE ROW, so deciding who to ask
 * costs no request at all. And the answers go back as a map, not one at a time,
 * so a batch of fifty with three unset is one dialog with three lines and then
 * fifty starts — not three interruptions.
 *
 * Keyboard-first: within a row, `M` and `F` answer it and move to the next
 * unanswered one, so a long list is a run of keystrokes rather than a hundred
 * pointer trips. The two whole-list buttons cover the common case where every
 * missing row is the same answer.
 *
 * ── What it does NOT do ─────────────────────────────────────────────
 * It never writes. The caller owns persistence — the table sends each answer
 * inside that customer's `/start` body, so setting the gender and starting the
 * run is one request per customer rather than two. That is also why there is no
 * spinner on the confirm: nothing is awaited here, the answers are handed back
 * synchronously and the starts report through the same toasts every Start does.
 */

/** The four things a row needs to be asked about. `CustomerRow` satisfies it. */
export interface GenderGateRow {
  id: string
  customerId: string
  name: string
  email: string
}

export interface GenderGateAsk<T extends GenderGateRow = GenderGateRow> {
  /** Everything the operator asked to start — what the answers unblock. */
  targets: readonly T[]
  /** The subset with no gender on file. Never empty when the dialog is open. */
  missing: readonly T[]
  /**
   * The table believed these were set and the server said otherwise.
   *
   * Changes the wording, not the behaviour — but the operator should know the
   * list was stale, because it means someone cleared the field in the meantime.
   */
  stale?: boolean
}

export interface GenderGateDialogProps<T extends GenderGateRow> {
  ask: GenderGateAsk<T> | null
  onCancel: () => void
  /** One entry per `missing` row, keyed by `id`. Always complete when called. */
  onConfirm: (assignments: ReadonlyMap<string, Gender>) => void
}

/** Which option each key answers with. Lowercase; the handler folds case. */
const KEY_TO_GENDER: Readonly<Record<string, Gender>> = Object.freeze({ m: 'Male', f: 'Female' })

export function GenderGateDialog<T extends GenderGateRow>(p: GenderGateDialogProps<T>) {
  if (p.ask === null) return null
  /*
   * Keyed by WHO is being asked, so a second ask about different customers
   * mounts a fresh form (fresh answers), while re-renders of the same ask — a
   * realtime push, a parent state change — keep the answers already given.
   */
  // Joined on NUL — written as an escape, not as a raw byte. A literal NUL in
  // the source makes git treat this whole file as binary, which costs every
  // future diff, blame and merge on it for a separator nobody ever reads.
  const key = p.ask.missing.map((row) => row.id).join('\u0000')
  return <GenderGateForm key={key} ask={p.ask} onCancel={p.onCancel} onConfirm={p.onConfirm} />
}

function GenderGateForm<T extends GenderGateRow>(p: {
  ask: GenderGateAsk<T>
  onCancel: () => void
  onConfirm: (assignments: ReadonlyMap<string, Gender>) => void
}) {
  const { missing, targets, stale } = p.ask

  /*
   * A plain object rather than a Map so React's shallow state semantics apply
   * without a wrapper; converted to the Map the caller wants once, on confirm.
   */
  const [answers, setAnswers] = React.useState<Readonly<Record<string, Gender>>>({})
  /** The same answers, readable from an event handler without a stale closure. */
  const answersRef = React.useRef(answers)
  answersRef.current = answers
  const answered = React.useMemo(
    () => missing.reduce((n, row) => (answers[row.id] === undefined ? n : n + 1), 0),
    [answers, missing]
  )
  const complete = answered === missing.length

  /** One ref per row's control group, for the M/F auto-advance. */
  const groupRefs = React.useRef<Array<HTMLDivElement | null>>([])

  const answer = React.useCallback((id: string, gender: Gender) => {
    setAnswers((prev) => (prev[id] === gender ? prev : { ...prev, [id]: gender }))
  }, [])

  const answerAll = React.useCallback(
    (gender: Gender) => {
      const next: Record<string, Gender> = {}
      for (const row of missing) next[row.id] = gender
      setAnswers(next)
    },
    [missing]
  )

  const confirmRef = React.useRef<HTMLButtonElement | null>(null)

  /**
   * Land on the first row's control when the dialog opens.
   *
   * Radix puts initial focus on the first tabbable thing in the content, which
   * is the header's close button — so without this the operator's first `M`
   * goes nowhere and the keyboard story is a lie until they click. Deferred a
   * tick so it runs after Radix's own auto-focus rather than being undone by it.
   */
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      groupRefs.current[0]?.querySelector<HTMLButtonElement>('button')?.focus()
    }, 0)
    return () => window.clearTimeout(handle)
  }, [])

  /**
   * After a keystroke answers row `index`, move to the next row that still has
   * no answer — wrapping, so a row skipped earlier is reached again — and land
   * on its first option. Nothing to move to means the batch is complete, and
   * focus goes to the confirm button so Enter finishes the job.
   *
   * The row controls all exist already, so they can take focus at once. The
   * confirm button is `disabled` until the answer that completed the batch has
   * committed, and a disabled button refuses focus — hence the deferral on
   * that one branch only.
   */
  const advanceFrom = React.useCallback(
    (index: number, next: Readonly<Record<string, Gender>>) => {
      const n = missing.length
      for (let step = 1; step <= n; step++) {
        const i = (index + step) % n
        const row = missing[i]
        if (row !== undefined && next[row.id] === undefined) {
          groupRefs.current[i]?.querySelector<HTMLButtonElement>('button')?.focus()
          return
        }
      }
      window.setTimeout(() => confirmRef.current?.focus(), 0)
    },
    [missing]
  )

  const onRowKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
    const gender = KEY_TO_GENDER[e.key.toLowerCase()]
    if (gender === undefined) return
    const row = missing[index]
    if (row === undefined) return
    e.preventDefault()
    // Computed once, outside the updater, so the advance and the state agree
    // and the updater stays pure.
    const next = { ...answersRef.current, [row.id]: gender }
    setAnswers(next)
    advanceFrom(index, next)
  }

  const confirm = () => {
    if (!complete) return
    const map = new Map<string, Gender>()
    for (const row of missing) {
      const g = answers[row.id]
      if (g !== undefined) map.set(row.id, g)
    }
    p.onConfirm(map)
  }

  const startCount = targets.length
  const title =
    missing.length === 1 ? 'Set a gender to start' : `Set ${missing.length} genders to start`

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) p.onCancel()
      }}
      title={
        <span className="flex items-center gap-2">
          <Mic aria-hidden="true" className="size-4 text-accent" />
          {title}
        </span>
      }
      description={
        stale
          ? 'The list said these customers had a gender, but nothing is on file for them now. The audio assessment speaks in a voice chosen by gender and has no default, so Start will not run them until it is set.'
          : 'The audio assessment speaks in a voice chosen by gender and has no default — a customer without one would sign in, hunt, win the shift and then stop at the recorded questions. Set it once here; it is saved to the customer.'
      }
      footer={
        <>
          <span
            className="tabular mr-auto font-mono text-[12px] text-muted max-sm:hidden"
            aria-live="polite"
          >
            {answered} of {missing.length} set
          </span>
          <Button variant="secondary" size="md" onClick={p.onCancel}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            variant="success"
            size="md"
            disabled={!complete}
            onClick={confirm}
            title={complete ? undefined : 'Every customer listed needs an answer first'}
          >
            <Play aria-hidden="true" />
            {startCount === 1
              ? 'Save & start'
              : `Save & start ${startCount}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-2.5">
        {/*
          Whole-list answers, only when there is a list. A single row already
          has its two buttons in front of it; a second pair saying the same
          thing would be noise.
        */}
        {missing.length > 1 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line bg-s1 px-3 py-2">
            <span className="text-[12.5px] text-muted">
              Everyone listed is
            </span>
            <div className="flex items-center gap-1.5">
              {GENDERS.map((g) => (
                <Button key={g} variant="secondary" size="sm" onClick={() => answerAll(g)}>
                  {g}
                </Button>
              ))}
            </div>
          </div>
        ) : null}

        <ul className="flex flex-col gap-1.5" aria-label="Customers with no gender on file">
          {missing.map((row, index) => {
            const current = answers[row.id]
            const set = current !== undefined
            return (
              <li
                key={row.id}
                className={cn(
                  'flex items-center gap-3 rounded-md border border-l-2 px-3 py-2 transition-colors duration-100',
                  set ? 'border-line border-l-ok bg-s1' : 'border-line border-l-warn bg-s1'
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col leading-tight">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-[13.5px] font-medium text-fg">
                      {row.name || '(no name)'}
                    </span>
                    {set ? (
                      <Check aria-hidden="true" className="size-3.5 shrink-0 text-ok" />
                    ) : null}
                  </span>
                  <span className="flex min-w-0 items-center gap-1.5 text-[11.5px]">
                    <span className="tabular shrink-0 font-mono text-muted">{row.customerId}</span>
                    {row.email ? <span className="truncate text-faint">{row.email}</span> : null}
                  </span>
                </div>

                <div
                  ref={(el) => {
                    groupRefs.current[index] = el
                  }}
                  role="group"
                  aria-label={`Gender for ${row.name || row.customerId}`}
                  onKeyDown={(e) => onRowKeyDown(e, index)}
                  className="inline-flex h-8 flex-none items-center gap-0.5 rounded-md border border-line bg-s2 p-0.5"
                >
                  {GENDERS.map((g) => {
                    const active = current === g
                    return (
                      <button
                        key={g}
                        type="button"
                        aria-pressed={active}
                        onClick={() => answer(row.id, g)}
                        className={cn(
                          'inline-flex h-7 items-center gap-1.5 rounded-sm px-2.5 text-[12.5px] font-medium whitespace-nowrap',
                          'transition-colors duration-100',
                          active
                            ? 'bg-accent-soft text-accent'
                            : 'text-muted hover:bg-s3 hover:text-fg'
                        )}
                      >
                        {g}
                        <span className="max-sm:hidden">
                          <Kbd>{g[0]}</Kbd>
                        </span>
                      </button>
                    )
                  })}
                </div>
              </li>
            )
          })}
        </ul>

        {/*
          Said once, below the list, because it is the part an operator is most
          likely to get wrong on a long list: the number on the button is the
          BATCH, and the rows above are only the part of it that was missing.
        */}
        {startCount > missing.length ? (
          <p className="flex items-start gap-2 text-[12px] text-muted">
            <AlertTriangle aria-hidden="true" className="mt-px size-3.5 shrink-0 text-faint" />
            <span>
              The other {startCount - missing.length} selected already have a gender and start
              with these.
            </span>
          </p>
        ) : null}
      </div>
    </Dialog>
  )
}
